import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type HighlightsSideProps = {
  /** The classname of the component. */
  className?: string;
  /** The articles to be displayed. */
  cardProps: ComponentProps<typeof Card>[];
};

/** This is the last 6 articles in the highlights component.
 * https://www.figma.com/design/50gwyNfDsv70dHaddmTd7k/BT-SG60--WEB---APP-?node-id=164-79094&m=dev
 */
const HighlightsSide = ({
  className,
  cardProps,
}: HighlightsSideProps): React.ReactElement => {
  const { isScreenMD, isScreenXL } = useWindowSize();

  if (!isScreenXL) return <></>;

  const variant: CardVariations = {
    image: {
      position: "left",
      width: cn("w-120px"),
    },
    content: {
      width: cn("w-full-120px"),
      extraClass: cn("md:pl-4"),
    },
    title: {
      color: cn("text-gray-850"),
      size: cn("text-base"),
      extraClass: cn("font-semibold leading-[1.25rem] font-lct"),
    },
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
  };

  const groupedArticles = new Array(2)
    // Make sure each element isn't empty
    .fill("")
    // For each group, grab the right `slice` of the input array
    .map((_, i) => cardProps.slice(i * 2, (i + 1) * 2));

  return (
    <div
      data-testid="highlights-side-component"
      className={cn("grid gap-8", className)}
    >
      {groupedArticles.map((_cardprop, index) => {
        return (
          <div
            key={index}
            className={cn(
              "relative grid gap-8",
              "lg:grid-cols-2 lg:after:absolute lg:after:-bottom-4 lg:after:h-[1px] lg:after:w-full lg:after:bg-gray-175",
              "xl:grid-cols-1 xl:after:hidden"
            )}
          >
            {_cardprop.map((cardProp, _index) => {
              return (
                <Card
                  key={_index}
                  {...cardProp}
                  variations={variant}
                  thumbnail={isScreenMD ? cardProp.thumbnail : undefined}
                  className={cn(
                    "relative",
                    "after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175",
                    {
                      "lg:before:absolute lg:before:-left-4 lg:before:h-full lg:before:w-[1px] lg:before:bg-gray-175 xl:before:hidden":
                        _index === 1,
                    }
                  )}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default HighlightsSide;
